/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import logger from './logger';

/**
 * A Copilot prompt typed on the login page, held until the user lands on the
 * create integration page after signing in.
 *
 * `localStorage` rather than `sessionStorage` or the OAuth `state` parameter:
 * signing in is a full page redirect to the identity provider, so router state
 * is gone, and the email sign up flow can come back in a different tab, which
 * `sessionStorage` would not survive. The prompt is free text of unbounded
 * length, so it is deliberately kept out of the `state` parameter and therefore
 * out of URLs and identity provider logs.
 */
const PENDING_PROMPT_KEY = 'devant_pending_copilot_prompt';

/** A stored prompt older than this is ignored — the sign in was abandoned. */
const TTL_MS = 30 * 60 * 1000;

/**
 * Longer prompts are truncated rather than dropped.
 *
 * Exported so the input the prompt is typed into can cap itself at the same
 * number: truncating here, silently, is how a pasted spec used to lose its tail
 * with nothing in the UI ever saying so.
 */
export const MAX_PROMPT_LENGTH = 4000;

interface StoredPrompt {
  prompt: string;
  createdAt: number;
}

/**
 * Stores `prompt` for the sign in round trip, replacing any previous one.
 *
 * Every access to `localStorage` is guarded: it throws outright in Safari
 * private mode and wherever the browser is set to block site data, and losing a
 * prompt must never break signing in.
 */
export const setPendingCopilotPrompt = (prompt: string) => {
  const trimmed = prompt.trim();
  if (!trimmed) {
    return;
  }
  try {
    const entry: StoredPrompt = {
      prompt: trimmed.slice(0, MAX_PROMPT_LENGTH),
      createdAt: Date.now(),
    };
    localStorage.setItem(PENDING_PROMPT_KEY, JSON.stringify(entry));
  } catch (error) {
    logger.error('Failed to store the pending Copilot prompt', error);
  }
};

export const clearPendingCopilotPrompt = () => {
  try {
    localStorage.removeItem(PENDING_PROMPT_KEY);
  } catch (error) {
    logger.error('Failed to clear the pending Copilot prompt', error);
  }
};

/**
 * The stored prompt, or `null` when there is none, it has expired or it cannot
 * be read. An expired entry is removed on the way out.
 */
export const getPendingCopilotPrompt = (): string | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_PROMPT_KEY);
  } catch (error) {
    logger.error('Failed to read the pending Copilot prompt', error);
    return null;
  }
  if (!raw) {
    return null;
  }

  let entry: StoredPrompt;
  try {
    entry = JSON.parse(raw);
  } catch (error) {
    // Corrupt or from an older format — drop it rather than keep failing.
    clearPendingCopilotPrompt();
    return null;
  }

  if (
    !entry ||
    typeof entry.prompt !== 'string' ||
    typeof entry.createdAt !== 'number'
  ) {
    clearPendingCopilotPrompt();
    return null;
  }

  if (Date.now() - entry.createdAt > TTL_MS) {
    clearPendingCopilotPrompt();
    return null;
  }

  return entry.prompt || null;
};

/**
 * Reads the stored prompt and removes it, so it is handed to Copilot exactly
 * once. Every consumer should use this rather than `get` followed by `clear`.
 */
export const consumePendingCopilotPrompt = (): string | null => {
  const prompt = getPendingCopilotPrompt();
  if (prompt) {
    clearPendingCopilotPrompt();
  }
  return prompt;
};
